import { useCallback, useRef, type MutableRefObject, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition';

interface AnchoredPanelProps {
  /** The field or button the panel hangs off. Also sets the panel's width. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * The panel element, for callers that close on outside click: the panel is
   * portalled away from the trigger's subtree, so "inside" has to be asked of it
   * directly. This and not an inner node — the scroll**bar**, the border and any
   * padding belong to the panel, and a click that lands on them is still inside.
   */
  panelRef?: MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  /** Extra classes for the panel's surface. */
  className?: string;
  children: ReactNode;
}

/**
 * A floating surface attached to a control: the shell every dropdown in the app
 * hangs its contents on.
 *
 * Two things are deliberately not the caller's to redo, because each had already
 * been re-derived by hand more than once and got a little worse each time:
 *
 * - **The portal.** An in-flow panel is clipped by any ancestor with an overflow
 *   context, and this app is made of them — the shell's scrolling `<main>`, the
 *   consult stage's scroll area, the collapsible review sections, the modal
 *   body. Portaling to `<body>` floats the panel over all of it.
 * - **The placement.** Handled by `useAnchoredPosition`, which flips the panel
 *   above its anchor and shrinks it to the space a software keyboard leaves
 *   behind. Read that file before changing any geometry here.
 *
 * The panel is itself the scroll container, so callers hand over contents and
 * nothing about height: a panel whose child had to remember to say `flex-1
 * min-h-0` would be a contract living in a comment.
 */
export function AnchoredPanel({
  anchorRef,
  panelRef,
  open,
  className,
  children,
}: AnchoredPanelProps) {
  const localRef = useRef<HTMLDivElement | null>(null);

  const setPanel = useCallback(
    (el: HTMLDivElement | null) => {
      localRef.current = el;
      if (panelRef) panelRef.current = el;
    },
    [panelRef],
  );

  useAnchoredPosition({ anchorRef, panelRef: localRef, open });

  if (!open) return null;

  return createPortal(
    <div
      ref={setPanel}
      // Position, width and height budget are written by the hook; everything
      // that is not geometry belongs in the class list.
      // Above `Modal`'s `z-50`: a dropdown opened inside a dialog has to draw
      // over it. Today that only works by portal order, which is a tie nobody
      // should be relying on.
      style={{ position: 'absolute', top: 0, left: 0, zIndex: 60, overflow: 'auto' }}
      className={cn(
        'rounded-md border border-line bg-paper shadow-[0_12px_28px_-12px_rgba(0,0,0,0.25)]',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
